import * as React from 'react';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import type { TaskSummary, TaskWorkOutcome } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { declarableTasksOf, defaultOutcomeFor } from '@/lib/my-tasks';
import { formatDuration } from '@/lib/duration';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { TaskPicker } from '@/components/TaskPicker';

export const workLogClockOutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work-log/clock-out',
  component: ClockOutPage,
});

interface Line {
  taskId: string | null;
  label: string;
  minutes: number;
  outcome: TaskWorkOutcome | null;
}

function ClockOutPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Worked time only ever grows while this form is open, so the figure is
  // refetched rather than frozen: a stale target either blocks an honest
  // submission or hides minutes the employee still has to account for. The
  // server owns the total at submit and absorbs any remaining drift.
  const currentQuery = useQuery({
    queryKey: ['work-log', 'current'],
    queryFn: () => api.getCurrentWorkSession(),
    refetchInterval: 30_000,
  });
  const session = currentQuery.data ?? null;

  const [lines, setLines] = React.useState<Line[] | null>(null);

  const workedMinutes = session?.workedMinutes ?? 0;

  // What was picked at clock-in is a starting point, not the final list: tasks
  // get assigned mid-session, and people finish more than they set out to.
  const tasksQuery = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => api.getMyTasks(),
    retry: false,
  });

  const assignedTasks: TaskSummary[] = React.useMemo(
    () => declarableTasksOf(tasksQuery.data),
    [tasksQuery.data],
  );
  const finishedIds = React.useMemo(
    () => new Set(assignedTasks.filter((t) => t.statusKind === 'finished').map((t) => t.id)),
    [assignedTasks],
  );

  React.useEffect(() => {
    if (!session || lines !== null || tasksQuery.isPending) return;
    const fromClockIn: Line[] = session.splits
      .filter((s) => s.taskId !== null)
      .map((s) => ({
        taskId: s.taskId,
        label: s.taskTitle ?? s.taskId ?? 'Task',
        minutes: 0,
        outcome: finishedIds.has(s.taskId ?? '') ? 'completed' : 'pending',
      }));
    setLines(fromClockIn);
  }, [session, lines, tasksQuery.isPending, finishedIds]);

  const addTask = (taskId: string): void => {
    const task = assignedTasks.find((t) => t.id === taskId);
    if (!task) return;
    setLines((prev) => {
      const rows = prev ?? [];
      if (rows.some((l) => l.taskId === taskId)) return rows;
      return [...rows, { taskId, label: task.title, minutes: 0, outcome: defaultOutcomeFor(task) }];
    });
  };

  const removeLine = (index: number): void => {
    setLines((prev) => (prev ?? []).filter((_, i) => i !== index));
  };

  const rows = lines ?? [];
  const declared = rows.reduce((sum, l) => sum + (Number.isFinite(l.minutes) ? l.minutes : 0), 0);
  const residual = workedMinutes - declared;
  const unusedTasks = assignedTasks.filter((t) => !rows.some((l) => l.taskId === t.id));

  const clockOutMutation = useMutation({
    mutationFn: () => {
      // Only the task minutes are sent as a claim. The server owns the worked
      // total and puts whatever is left on this unmapped row, so a form left
      // open for a few minutes still submits cleanly.
      const splits = rows
        .filter((l) => l.minutes > 0)
        .map((l) => ({ taskId: l.taskId, minutes: l.minutes, outcome: l.outcome }));
      splits.push({ taskId: null, minutes: Math.max(0, residual), outcome: null });
      return api.clockOut({ splits });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['work-log'] });
      await navigate({ to: '/work-log' });
    },
  });

  const setMinutes = (index: number, value: string): void => {
    const n = value === '' ? 0 : Number.parseInt(value, 10);
    setLines((prev) =>
      (prev ?? []).map((l, i) => (i === index ? { ...l, minutes: Number.isNaN(n) ? 0 : n } : l)),
    );
  };

  if (session === null) {
    return (
      <AppShell>
        <div className="mx-auto w-full max-w-3xl p-6">
          <Card>
            <CardHeader>
              <CardTitle>Not clocked in</CardTitle>
              <CardDescription>There is no open session to close.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => void navigate({ to: '/work-log' })}>Back to work log</Button>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-3xl space-y-8">
        <div>
          <h1 className="font-display text-4xl font-semibold md:text-5xl">Declare your time</h1>
          <p className="mt-3 max-w-2xl text-lg leading-relaxed text-[hsl(var(--muted-foreground))]">
            Every worked minute has to land somewhere. Anything you cannot attribute goes to
            unmapped time and waits in your queue.
          </p>
        </div>

        {clockOutMutation.error ? (
          <div className="rounded-lg border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-4 py-3 text-sm">
            {toMessage(clockOutMutation.error)}
          </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Account for {formatDuration(workedMinutes)}</CardTitle>
            <CardDescription>
              {session.breakMinutes > 0
                ? `You were clocked in for ${formatDuration(workedMinutes + session.breakMinutes)} and on break for ${formatDuration(session.breakMinutes)}, so ${formatDuration(workedMinutes)} counts as worked.`
                : `You were clocked in for ${formatDuration(workedMinutes)} with no breaks.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {rows.length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                You clocked in without picking a task. Add the ones you worked on below, or clock
                out and map the time later.
              </p>
            ) : (
              rows.map((line, i) => (
                <div key={line.taskId ?? i} className="flex items-center gap-3">
                  <span className="flex-1 text-sm">{line.label}</span>
                  <Input
                    type="number"
                    min={0}
                    value={line.minutes === 0 ? '' : String(line.minutes)}
                    onChange={(e) => setMinutes(i, e.target.value)}
                    placeholder="0"
                    className="w-24"
                    aria-label={`Minutes on ${line.label}`}
                  />
                  <Select
                    value={line.outcome ?? 'pending'}
                    onChange={(e) =>
                      setLines((prev) =>
                        (prev ?? []).map((l, idx) =>
                          idx === i ? { ...l, outcome: e.target.value as TaskWorkOutcome } : l,
                        ),
                      )
                    }
                    className="w-36"
                    aria-label={`Outcome for ${line.label}`}
                  >
                    <option value="pending">Pending</option>
                    <option value="completed">Done — mark task complete</option>
                  </Select>
                  <button
                    type="button"
                    onClick={() => removeLine(i)}
                    aria-label={`Remove ${line.label}`}
                    className="rounded-lg p-2 text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ))
            )}

            {unusedTasks.length > 0 ? <TaskPicker tasks={unusedTasks} onSelect={addTask} /> : null}

            <div
              className={
                residual < 0
                  ? 'flex items-center justify-between rounded-lg border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 px-4 py-3 text-sm'
                  : 'flex items-center justify-between rounded-lg border border-[hsl(var(--border))] px-4 py-3 text-sm'
              }
            >
              <span>
                {residual > 0
                  ? 'Left to assign — goes to unmapped time'
                  : residual < 0
                    ? 'Over your worked time'
                    : 'All time assigned'}
              </span>
              <span className="tabular-nums font-medium">
                {residual < 0 ? `${formatDuration(-residual)} over` : formatDuration(residual)}
              </span>
            </div>

            {residual < 0 ? (
              <p className="text-sm text-[hsl(var(--destructive))]">
                Remove {formatDuration(-residual)} to match the {formatDuration(workedMinutes)} you
                worked.
              </p>
            ) : null}

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => void navigate({ to: '/work-log' })}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                disabled={residual < 0 || clockOutMutation.isPending}
                onClick={() => clockOutMutation.mutate()}
              >
                {clockOutMutation.isPending ? 'Clocking out…' : 'Clock out'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
